import React, { useState } from "react";
import { Icon } from "../../UIElements/Icon";

const WidgetPreview = ({ config, widgetType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState("desktop"); // 'desktop' | 'mobile'

  const gradientStyle = {
    background: `linear-gradient(135deg, ${config.primaryColor} 0%, ${config.secondaryColor} 100%)`,
  };

  const previewScale = 1;

  const renderFloatingPreview = () => (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
      {/* Simulated website content */}
      <div className="space-y-4 p-4">
        <div className="h-16 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-8 w-full rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-8 w-5/6 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-48 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-8 w-full rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-8 w-4/5 rounded bg-gray-200 dark:bg-gray-700" />
      </div>

      {/* Chat window */}
      {isOpen && (
        <div
          className={`absolute ${config.position === "bottom-left" ? "left-4" : "right-4"} bottom-20 overflow-hidden rounded-2xl shadow-2xl transition-all duration-300`}
          style={{
            width:
              viewMode === "mobile"
                ? "100%"
                : `${config.chatWidth * previewScale}px`,
            height:
              viewMode === "mobile"
                ? "100%"
                : `${config.chatHeight * previewScale}px`,
            maxHeight: viewMode === "mobile" ? "100%" : "calc(100% - 100px)",
            ...(viewMode === "mobile"
              ? { left: 0, right: 0, bottom: 0, borderRadius: 0 }
              : {}),
          }}
        >
          <iframe
            src={config.chatUrl}
            className="h-full w-full border-0"
            style={{
              transform: "scale(0.8)",
              transformOrigin: "0 0",
              width: "125%",
              height: "125%",
            }}
            title="Chat Preview"
          />
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`absolute ${config.position === "bottom-left" ? "left-4" : "right-4"} bottom-4 flex items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110`}
        style={{
          ...gradientStyle,
          width: `${config.buttonSize}px`,
          height: `${config.buttonSize}px`,
        }}
      >
        {isOpen ? (
          <Icon name="X" className="h-6 w-6 text-white" />
        ) : config.iconUrl ? (
          <img
            src={config.iconUrl}
            alt="Chat"
            className="h-7 w-7 object-contain"
            style={
              config.position === "bottom-right"
                ? { transform: "scaleX(-1)" }
                : {}
            }
          />
        ) : (
          <Icon
            name="MessageCircle"
            className="h-6 w-6 text-white"
            style={
              config.position === "bottom-right"
                ? { transform: "scaleX(-1)" }
                : {}
            }
          />
        )}
      </button>
    </div>
  );

  const renderPopupPreview = () => (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
      {/* Simulated website content */}
      <div className="space-y-4 p-4">
        <div className="h-8 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700" />

        {/* CTA Button */}
        <button
          onClick={() => setIsOpen(true)}
          className="rounded-lg px-6 py-3 font-medium text-white shadow-lg"
          style={gradientStyle}
        >
          💬 AI Chat
        </button>

        <div className="h-4 w-5/6 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-32 rounded bg-gray-200 dark:bg-gray-700" />
      </div>

      {/* Modal overlay */}
      {isOpen && (
        <>
          <div
            className="absolute inset-0 z-10 bg-black/50"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl shadow-2xl"
            style={{
              width: viewMode === "mobile" ? "100%" : "90%",
              height: viewMode === "mobile" ? "100%" : "80%",
              ...(viewMode === "mobile"
                ? { maxWidth: "100%" }
                : { maxWidth: `${config.chatWidth * previewScale}px` }),
              ...(viewMode === "mobile"
                ? { maxHeight: "100%" }
                : { maxHeight: `${config.chatHeight * previewScale}px` }),
            }}
          >
            {/* Header (show in desktop if configured, always show in mobile for close button) */}
            {(config.showHeader !== false || viewMode === "mobile") && (
              <div
                className="flex h-10 items-center justify-end px-3"
                style={gradientStyle}
              >
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-xl leading-none text-white"
                >
                  &times;
                </button>
              </div>
            )}
            {/* Iframe */}
            <iframe
              src={config.chatUrl}
              className="w-full border-0"
              style={{
                height:
                  config.showHeader !== false || viewMode === "mobile"
                    ? "calc(100% - 40px)"
                    : "100%",
              }}
              title="Chat Preview"
            />
          </div>
        </>
      )}
    </div>
  );

  const renderInlinePreview = () => (
    <div className="relative h-full w-full overflow-hidden rounded-lg bg-gray-100 p-4 dark:bg-gray-800">
      {/* Simulated website content */}
      <div className="mb-4 space-y-4">
        <div className="h-6 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700" />
      </div>

      {/* Inline chat */}
      <div
        className="border-border mx-auto overflow-hidden rounded-2xl border shadow-lg"
        style={{
          maxWidth: `${config.chatWidth * previewScale}px`,
          height: `${config.chatHeight * previewScale}px`,
        }}
      >
        <iframe
          src={config.chatUrl}
          className="h-full w-full border-0"
          title="Chat Preview"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* View mode toggle */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-card-foreground font-semibold">
          Interactive Preview
        </h3>
        {/* Instructions */}
        <div className="text-muted-foreground text-center text-sm">
          {widgetType === "floating" && (
            <p>👇 Click the floating button to open/close the chat</p>
          )}
          {widgetType === "popup" && (
            <p>👇 Click the &quot;AI Chat&quot; button to open the modal</p>
          )}
          {widgetType === "inline" && (
            <p>👇 The chat is displayed directly integrated in the page</p>
          )}
        </div>
        <div className="bg-accent flex w-fit items-center gap-2 rounded-lg p-1">
          <button
            onClick={() => setViewMode("desktop")}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 transition-colors ${
              viewMode === "desktop"
                ? "bg-brand-gradient text-white"
                : "text-muted-foreground"
            }`}
          >
            <Icon name="Monitor" className="h-4 w-4" />
            <span className="text-sm">Desktop</span>
          </button>
          <button
            onClick={() => setViewMode("mobile")}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 transition-colors ${
              viewMode === "mobile"
                ? "bg-brand-gradient text-white"
                : "text-muted-foreground"
            }`}
          >
            <Icon name="Smartphone" className="h-4 w-4" />
            <span className="text-sm">Mobile</span>
          </button>
        </div>
      </div>

      {/* Preview container */}
      <div
        className={`mx-auto overflow-hidden rounded-xl border-4 border-gray-300 transition-all duration-300 dark:border-gray-600 ${
          viewMode === "mobile" ? "max-w-[320px]" : "max-w-full"
        }`}
        style={{ height: "750px" }}
      >
        {widgetType === "floating" && renderFloatingPreview()}
        {widgetType === "popup" && renderPopupPreview()}
        {widgetType === "inline" && renderInlinePreview()}
      </div>
    </div>
  );
};

export default WidgetPreview;
